import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { useConfig } from './store';
import { TruckScene } from './components/TruckScene';
import { PaintPanel } from './components/PaintPanel';
import { fittedMotifRadius } from './badge/motif';
import { useMediaQuery, wideLayout } from './media';

const BadgeScene = lazy(() => import('./components/BadgeScene').then((m) => ({ default: m.BadgeScene })));

function Toggle({ label, value, onChange }: { label: string; value: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex items-center justify-between gap-3 text-sm">
      <span className="text-white/70">{label}</span>
      <input type="checkbox" checked={value} onChange={(e) => onChange(e.target.checked)} className="h-4 w-4 accent-pink-400" />
    </label>
  );
}

function Slider({
  label,
  value,
  min,
  max,
  step,
  unit,
  note,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  unit: string;
  note?: string;
  onChange: (v: number) => void;
}) {
  return (
    <label className="block text-sm">
      <span className="flex items-center justify-between text-white/70">
        {label}
        <span className="font-mono text-xs text-white/50">
          {value}
          {unit}
        </span>
      </span>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="mt-1 w-full accent-pink-400"
      />
      {note && <span className="mt-1 block font-mono text-[11px] text-white/60">{note}</span>}
    </label>
  );
}

function useMoreBelow<T extends HTMLElement>(contentKey: unknown) {
  const ref = useRef<T>(null);
  const [more, setMore] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) throw new Error('useMoreBelow ref is not attached');
    const update = () => setMore(el.scrollTop + el.clientHeight < el.scrollHeight - 4);
    const observer = new ResizeObserver(update);
    [el, ...el.children].forEach((node) => observer.observe(node));
    el.addEventListener('scroll', update, { passive: true });
    update();
    return () => {
      observer.disconnect();
      el.removeEventListener('scroll', update);
    };
  }, [contentKey]);

  return [ref, more] as const;
}

export default function App() {
  const { tab, setTab, showAwning, showLights, showBar, showSignage, setTruck, badge, setBadge } = useConfig();
  const motif = fittedMotifRadius(badge);
  const [asideRef, moreBelow] = useMoreBelow<HTMLElement>(tab);
  const wide = useMediaQuery(wideLayout);

  const panel = (
    <div key="panel" className="relative flex w-full shrink-0 grow flex-col wide:w-80 wide:grow-0">
      <aside
        ref={asideRef}
        className="flex flex-1 flex-col bg-[#0a1f40] p-5 shadow-[inset_0_1px_0_rgb(255_255_255/0.06)] wide:min-h-0 wide:overflow-y-auto wide:shadow-[inset_-1px_0_0_rgb(255_255_255/0.06)]"
      >
        <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-pink-400">Tipsy Configurator</p>
        <h1 className="mt-1 text-lg font-semibold">Build your bar</h1>

        <div role="group" aria-label="View" className="mt-4 grid grid-cols-2 gap-1 rounded-lg border border-white/10 p-1 text-sm">
          {(['truck', 'badge'] as const).map((t) => (
            <button
              key={t}
              type="button"
              aria-pressed={tab === t}
              onClick={() => setTab(t)}
              className={`rounded-md px-3 py-1.5 capitalize transition ${
                tab === t ? 'bg-pink-400 font-semibold text-[#061936]' : 'text-white/60 hover:text-white'
              }`}
            >
              {t === 'truck' ? 'Truck' : 'Badge'}
            </button>
          ))}
        </div>

        {tab === 'truck' ? (
          <div className="mt-6">
            <PaintPanel />
            <div className="mt-[26px] h-px bg-white/10" />
            <div className="mt-5 space-y-4">
              <Toggle label="Awning open" value={showAwning} onChange={(v) => setTruck({ showAwning: v })} />
              <Toggle label="String lights" value={showLights} onChange={(v) => setTruck({ showLights: v })} />
              <Toggle label="Stocked bar" value={showBar} onChange={(v) => setTruck({ showBar: v })} />
              <Toggle label="Signage board" value={showSignage} onChange={(v) => setTruck({ showSignage: v })} />
            </div>
          </div>
        ) : (
          <div className="mt-6 space-y-4">
            <Slider label="Width" value={badge.width} min={30} max={100} step={1} unit="mm" onChange={(v) => setBadge({ width: v })} />
            <Slider label="Height" value={badge.height} min={16} max={60} step={1} unit="mm" onChange={(v) => setBadge({ height: v })} />
            <Slider label="Thickness" value={badge.thickness} min={2} max={10} step={0.5} unit="mm" onChange={(v) => setBadge({ thickness: v })} />
            <Slider label="Corner holes" value={badge.cornerHoleRadius} min={1} max={5} step={0.25} unit="mm" onChange={(v) => setBadge({ cornerHoleRadius: v })} />
            <Slider
              label="Bubble motif"
              value={badge.motifRadius}
              min={0}
              max={10}
              step={0.5}
              unit="mm"
              note={motif < badge.motifRadius ? `Fitted to ${motif}mm to clear the rim and holes` : undefined}
              onChange={(v) => setBadge({ motifRadius: v })}
            />
            <Toggle label="Engrave (vs cut through)" value={badge.engrave} onChange={(v) => setBadge({ engrave: v })} />
            <p className="text-xs text-white/60">
              Real constructive-solid-geometry via Manifold (WASM), in a worker — export the result to STL for 3D printing.
            </p>
          </div>
        )}

        <div className="mt-auto pt-8">
          <footer className="border-t border-white/10 pt-4 font-mono text-[11px] leading-relaxed text-white/60">
            <p>Concept demo · not a Tipsy Truck product</p>
            <p>
              Built by Rokas Sileikis ·{' '}
              <a href="https://rokass.org/work/tipsy-configurator/" className="text-pink-400 underline-offset-2 hover:underline">
                case study ↗
              </a>
            </p>
          </footer>
        </div>
      </aside>
      <div
        aria-hidden="true"
        className={`pointer-events-none absolute inset-x-0 bottom-0 h-14 bg-linear-to-t from-[#0a1f40] via-[#0a1f40]/75 to-transparent transition-opacity duration-200 motion-reduce:transition-none wide:right-px ${
          moreBelow ? 'opacity-100' : 'opacity-0'
        }`}
      />
    </div>
  );

  const stage = (
    <main key="stage" className="sticky top-0 z-10 h-(--stage-h) shrink-0 bg-[#061936] wide:static wide:h-auto wide:min-h-0 wide:min-w-0 wide:flex-1">
      {tab === 'truck' ? (
        <TruckScene />
      ) : (
        <Suspense fallback={<p className="p-4 font-mono text-[11px] uppercase tracking-widest text-white/60">Loading badge…</p>}>
          <BadgeScene />
        </Suspense>
      )}
    </main>
  );

  return <div className="flex min-h-dvh flex-col wide:h-dvh wide:flex-row">{wide ? [panel, stage] : [stage, panel]}</div>;
}
